import { DateTime } from 'luxon'
import { draftToMarkdown } from 'markdown-draft-js'
import { buildDateTime } from 'lib/CustomLuxonUtils'
import { getRegistrationOpenDateTime } from 'pages/pga-coach/bookings/events/registration-open-datetime'
import {
  composeMaxAttendance,
  isRecurringPayment,
  ALL_AGE_GROUP_TAGS,
  LEGACY_ADULTS_ONLY_TAG,
  LEGACY_JUNIORS_ONLY_TAG,
  MANUAL_PAYMENT,
  ONE_TIME_PAYMENT,
} from 'utils/groupEventUtils'
import { dollarsToCents } from 'lib/utils/number'
import { NO_REGISTRATION_CLOSE } from './form-sections/registration-close-day'

const buildFilteredTags = formValues => {
  const ageGroupMap = new Map([
    ['adults-only', LEGACY_ADULTS_ONLY_TAG],
    ['juniors-only', LEGACY_JUNIORS_ONLY_TAG],
  ])
  const filteredTags = formValues.tags.filter(
    tag => !ALL_AGE_GROUP_TAGS.includes(tag),
  )
  const ageGroupValue = ageGroupMap.get(formValues.ageGroup)
  if (ageGroupValue) {
    filteredTags.push(ageGroupValue)
  }
  return filteredTags
}

export const buildUpdateVariables = ({
  formValues,
  event,
  eligibleFacilities,
  editRecurrence,
  updateReason,
}) => {
  const selectedFacility = eligibleFacilities.find(
    cf => cf.id === formValues.locationId,
  )
  const hasPaymentAccountId = (formValues.paymentAccountId || '').length > 0
  let registrationCloseDateTime = null

  if (formValues.registrationCloseDate && formValues.registrationCloseTime) {
    registrationCloseDateTime = buildDateTime(
      formValues.registrationCloseDate,
      formValues.registrationCloseTime,
      selectedFacility.timezone,
    )
  }

  const mappedSessions = formValues.sessions.map(session => ({
    ...(editRecurrence && session.groupEventId
      ? { groupEventId: session.groupEventId }
      : {}),
    facilityId: selectedFacility.id,
    startDateTime: buildDateTime(
      session.startDate,
      session.startTime,
      selectedFacility.timezone,
    ),
    endDateTime: buildDateTime(
      session.startDate,
      session.endTime,
      selectedFacility.timezone,
    ),
  }))

  const sessionsForWindow = editRecurrence
    ? [...mappedSessions].sort(
      (a, b) => a.startDateTime.toMillis() - b.startDateTime.toMillis(),
    )
    : mappedSessions

  if (formValues.hoursBeforeRegistrationCloses === NO_REGISTRATION_CLOSE) {
    registrationCloseDateTime = null
  } else if (formValues.hoursBeforeRegistrationCloses !== undefined) {
    const eventStartDateTime = sessionsForWindow[0].startDateTime

    registrationCloseDateTime = eventStartDateTime.minus({
      hours: Number(formValues.hoursBeforeRegistrationCloses),
    })
  }

  let registrationOpenDateTime = getRegistrationOpenDateTime(
    formValues,
    selectedFacility?.timezone || event.timezone,
  )

  if (editRecurrence) {
    const anchor = sessionsForWindow[0].startDateTime
    const eventStart = DateTime.fromISO(event.startDateTime)
    const rebase = dateTime =>
      dateTime && anchor.plus(dateTime.diff(eventStart))

    if (formValues.hoursBeforeRegistrationCloses === undefined) {
      registrationCloseDateTime = rebase(registrationCloseDateTime)
    }
    registrationOpenDateTime = rebase(registrationOpenDateTime)
  }

  const values = {
    title: formValues.title,
    academyId: event.academy?.id || null,
    assistantCoachIds: formValues.assistantCoachIds,
    description: formValues.description
      ? draftToMarkdown(JSON.parse(formValues.description))
      : null,
    registrationCloseDateTime,
    registrationOpenDateTime,
    tags: buildFilteredTags(formValues),
    externalRegistrationUrl: formValues.externalRegistrationUrl,
    maxAttendance: composeMaxAttendance(
      formValues.maxAttendance,
      event.hasWaitlist,
    ),
    priceInCents: dollarsToCents(formValues.price),
    featuredImageUrl: formValues.featuredImageUrl,
    paymentAccountId: hasPaymentAccountId ? formValues.paymentAccountId : null,
    paymentRecurrenceSchedule: isRecurringPayment(event)
      ? event.paymentRecurrenceSchedule
      : hasPaymentAccountId
        ? ONE_TIME_PAYMENT
        : MANUAL_PAYMENT,
    unlisted: formValues.unlisted,
    accessCode:
      (formValues.accessCode || '').length > 0 ? formValues.accessCode : null,
  }

  if (editRecurrence) {
    return {
      id: event.recurrence.id,
      reason: updateReason,
      values,
      occurrences: mappedSessions,
    }
  }

  return {
    id: event.id,
    reason: updateReason,
    values: {
      ...values,
      sessions: isRecurringPayment(event)
        ? event.sessions.map(session => ({
          facilityId: selectedFacility.id,
          startDateTime: session.startDateTime,
          endDateTime: session.endDateTime,
        }))
        : mappedSessions,
    },
  }
}

export default buildUpdateVariables
